import { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  documentId,
} from "firebase/firestore";
import { firestore } from "../firebase";
import { useAuth } from "../contexts/AuthContext";

export default function useContinueReading() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!user) {
        if (!mounted) return;
        setItems([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const pageReadsQuery = query(
          collection(firestore, "pageReads"),
          where("userId", "==", user.uid)
        );

        const pageReadsSnapshot = await getDocs(pageReadsQuery);

        const progressMap = {};

        pageReadsSnapshot.forEach((snap) => {
          const data = snap.data();
          const { issueId, pageId, pageNumber, timestamp } = data;

          if (!issueId) return;

          if (!progressMap[issueId]) {
            progressMap[issueId] = {
              issueId,
              pagesRead: new Set(),
              highestPageNumber: pageNumber || 0,
              highestPageId: pageId || null,
              latestTimestamp: timestamp || null,
            };
          }

          progressMap[issueId].pagesRead.add(pageId);

          if ((pageNumber || 0) > progressMap[issueId].highestPageNumber) {
            progressMap[issueId].highestPageNumber = pageNumber || 0;
            progressMap[issueId].highestPageId = pageId || null;
          }

          if (
            timestamp &&
            (!progressMap[issueId].latestTimestamp ||
              timestamp.toMillis() > progressMap[issueId].latestTimestamp.toMillis())
          ) {
            progressMap[issueId].latestTimestamp = timestamp;
          }
        });

        const issueIds = Object.keys(progressMap);

        if (!issueIds.length) {
          if (!mounted) return;
          setItems([]);
          setLoading(false);
          return;
        }

        // Firestore "in" queries are limited; chunk to be safe
        const chunkSize = 10;
        const issueDocs = [];

        for (let i = 0; i < issueIds.length; i += chunkSize) {
          const chunk = issueIds.slice(i, i + chunkSize);

          const issuesQuery = query(
            collection(firestore, "issues"),
            where(documentId(), "in", chunk)
          );

          const issuesSnapshot = await getDocs(issuesQuery);
          issuesSnapshot.forEach((docSnap) => {
            issueDocs.push({
              id: docSnap.id,
              ...docSnap.data(),
            });
          });
        }

        const issueMap = {};
        issueDocs.forEach((issue) => {
          issueMap[issue.id] = issue;
        });

        const continueReadingItems = issueIds
          .map((issueId) => {
            const progress = progressMap[issueId];
            const issue = issueMap[issueId];

            if (!issue) return null;

            const totalPages = issue?.pages ?? 0;
            const pagesReadCount = progress.pagesRead.size;

            if (!totalPages) return null;
            if (pagesReadCount <= 0) return null;
            if (pagesReadCount >= totalPages) return null;

            return {
              issueId,
              seriesId: issue?.seriesId ?? null,
              seriesTitle: issue?.seriesTitle ?? "Series",
              issueTitle: issue?.title ?? "Issue",
              thumbnailURL: issue?.thumbnailURL ?? "",
              totalPages,
              pagesReadCount,
              highestPageNumber: progress.highestPageNumber,
              highestPageId: progress.highestPageId,
              latestTimestamp: progress.latestTimestamp,
              
              progressPercent: Math.round((pagesReadCount / totalPages) * 100),
              href: `/reader/issue/${issueId}/read?page=${progress.highestPageNumber}`,
              // future-ready:
              resumeHref: `/reader/issue/${issueId}/read?page=${progress.highestPageNumber}`,
            };
          })
          .filter(Boolean)
          .sort((a, b) => {
            const aTime = a.latestTimestamp?.toMillis?.() ?? 0;
            const bTime = b.latestTimestamp?.toMillis?.() ?? 0;

            if (bTime !== aTime) return bTime - aTime;
            return (b.highestPageNumber || 0) - (a.highestPageNumber || 0);
          });

        if (!mounted) return;
        setItems(continueReadingItems);
      } catch (error) {
        console.error("Error loading continue reading items:", error);
        if (!mounted) return;
        setItems([]);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [user]);

  return { items, loading };
}